import React, { useState, useEffect } from "react";
import { T } from "./theme";
import { Card, Btn, Input } from "./ui";
import { COMISSAO_CONFIG_DEFAULT, AWARDS } from "./data";
import { supabase, isSupabaseReady } from "./supabase";

// ═══════════════════════════════════════════════════════════════════════
// COMISSÕES — admin edita matriz: % venda direta + cascata 10 níveis
// Persistido em Supabase configuracoes (chave='comissoes').
// ═══════════════════════════════════════════════════════════════════════

const AWARD_IDS = AWARDS.map(a => a.id);  // cafe, bronze, silver, gold, platinum, diamond

export default function ComissoesAdmin() {
  const [config, setConfig] = useState(COMISSAO_CONFIG_DEFAULT);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    async function load() {
      if (!isSupabaseReady) { setLoading(false); return; }
      try {
        const { data } = await supabase
          .from("configuracoes")
          .select("valor")
          .eq("chave", "comissoes")
          .maybeSingle();
        if (data?.valor) {
          // merge com defaults pra não quebrar se faltar campo
          setConfig({ ...COMISSAO_CONFIG_DEFAULT, ...data.valor });
        }
      } catch (e) { console.error("[ComissoesAdmin]", e); }
      setLoading(false);
    }
    load();
  }, []);

  function setPctVenda(awardId, valor) {
    const v = parseFloat(String(valor).replace(",", "."));
    setConfig(prev => ({ ...prev, pctVendaDireta: { ...prev.pctVendaDireta, [awardId]: isNaN(v) ? 0 : v } }));
  }
  function setBonus(awardId, valor) {
    const v = parseFloat(String(valor).replace(",", "."));
    setConfig(prev => ({ ...prev, bonusResidual: { ...prev.bonusResidual, [awardId]: isNaN(v) ? 0 : v } }));
  }
  function setNivel(idx, valor) {
    const v = parseFloat(String(valor).replace(",", "."));
    setConfig(prev => {
      const arr = [...prev.pctNivelBase];
      arr[idx] = isNaN(v) ? 0 : v;
      return { ...prev, pctNivelBase: arr };
    });
  }
  function setNiveisLib(awardId, valor) {
    const v = parseInt(valor, 10);
    setConfig(prev => ({ ...prev, niveisLiberados: { ...prev.niveisLiberados, [awardId]: isNaN(v) ? 1 : Math.max(1, Math.min(10, v)) } }));
  }

  async function salvar() {
    if (!isSupabaseReady) return;
    setSalvando(true);
    setMsg(null);
    try {
      const { error } = await supabase
        .from("configuracoes")
        .upsert({ chave: "comissoes", valor: config }, { onConflict: "chave" });
      if (error) throw error;
      setMsg({ tipo: "ok", texto: "✅ Configuração salva!" });
    } catch (e) {
      setMsg({ tipo: "err", texto: "Erro: " + e.message });
    }
    setSalvando(false);
  }

  function restaurarDefault() {
    if (!window.confirm("Restaurar valores padrão? Você ainda precisa clicar em Salvar.")) return;
    setConfig(COMISSAO_CONFIG_DEFAULT);
  }

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: T.textDim }}>Carregando...</div>;

  // Tabela de pré-visualização (cascata real aplicada)
  const previewMatrix = AWARD_IDS.map(aid => {
    const linha = { award: aid, nome: AWARDS.find(a => a.id === aid).nome };
    for (let n = 1; n <= 10; n++) {
      const base = config.pctNivelBase[n] ?? 0;
      const bonus = config.bonusResidual[aid] ?? 0;
      const lib = (config.niveisLiberados[aid] ?? 1);
      linha[`n${n}`] = n <= lib ? +(base + bonus).toFixed(2) : null;
    }
    return linha;
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ fontSize: 12, color: T.textSub }}>
        Configurações usadas pra calcular comissões. Base = líquido (valor - frete - taxas).
      </div>

      {msg && (
        <div style={{
          padding: "10px 14px", borderRadius: T.r.md,
          background: msg.tipo === "ok" ? T.ok + "15" : T.err + "15",
          border: `1px solid ${msg.tipo === "ok" ? T.ok : T.err}44`,
          color: msg.tipo === "ok" ? T.ok : T.err, fontSize: 12,
        }}>{msg.texto}</div>
      )}

      {/* % Venda direta */}
      <Card>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 12 }}>
          💼 % Venda direta (sobre líquido)
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10 }}>
          {AWARD_IDS.map(aid => (
            <CampoPct
              key={aid}
              label={AWARDS.find(a => a.id === aid).nome}
              value={config.pctVendaDireta[aid] ?? 0}
              onChange={v => setPctVenda(aid, v)}
            />
          ))}
        </div>
      </Card>

      {/* % Residual por nível */}
      <Card>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 12 }}>
          📊 % Residual por nível (sobre a comissão direta do vendedor)
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))", gap: 10 }}>
          {[1,2,3,4,5,6,7,8,9,10].map(n => (
            <CampoPct
              key={n}
              label={`Nível ${n}`}
              value={config.pctNivelBase[n] ?? 0}
              onChange={v => setNivel(n, v)}
            />
          ))}
        </div>
      </Card>

      {/* Bônus award no residual */}
      <Card>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 12 }}>
          🎖️ Bônus do award no residual (somado a cada nível)
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10 }}>
          {AWARD_IDS.map(aid => (
            <CampoPct
              key={aid}
              label={AWARDS.find(a => a.id === aid).nome}
              value={config.bonusResidual[aid] ?? 0}
              onChange={v => setBonus(aid, v)}
            />
          ))}
        </div>
      </Card>

      {/* Travamento níveis liberados */}
      <Card>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 12 }}>
          🔒 Níveis residual liberados por award
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10 }}>
          {AWARD_IDS.map(aid => (
            <div key={aid}>
              <div style={{ fontSize: 10, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                {AWARDS.find(a => a.id === aid).nome}
              </div>
              <Input
                type="number"
                min="1"
                max="10"
                value={config.niveisLiberados[aid] ?? 1}
                onChange={e => setNiveisLib(aid, e.target.value)}
              />
            </div>
          ))}
        </div>
      </Card>

      {/* Preview matriz aplicada */}
      <Card>
        <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 10 }}>
          🧮 Preview — % efetiva por (award recebedor × nível)
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ color: T.textDim }}>
                <th style={th()}>Award</th>
                {[1,2,3,4,5,6,7,8,9,10].map(n => <th key={n} style={th()}>N{n}</th>)}
              </tr>
            </thead>
            <tbody>
              {previewMatrix.map(l => (
                <tr key={l.award}>
                  <td style={tdNome()}>{l.nome}</td>
                  {[1,2,3,4,5,6,7,8,9,10].map(n => (
                    <td key={n} style={td(l[`n${n}`] != null)}>
                      {l[`n${n}`] != null ? `${l[`n${n}`]}%` : "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 8, fontSize: 10, color: T.textDim }}>
          — significa que esse award não recebe esse nível.
        </div>
      </Card>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={restaurarDefault} disabled={salvando}>Restaurar padrão</Btn>
        <Btn onClick={salvar} disabled={salvando}>{salvando ? "Salvando..." : "💾 Salvar configuração"}</Btn>
      </div>
    </div>
  );
}

function CampoPct({ label, value, onChange }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <div style={{ position: "relative" }}>
        <input
          type="text"
          value={String(value).replace(".", ",")}
          onChange={e => onChange(e.target.value)}
          style={{
            width: "100%", padding: "8px 24px 8px 10px",
            fontSize: 13, fontFamily: "inherit",
            background: T.surface, color: T.text,
            border: `1px solid ${T.border}`, borderRadius: T.r.md,
            textAlign: "right", fontWeight: 600,
          }}
        />
        <span style={{ position: "absolute", right: 10, top: 8, fontSize: 12, color: T.textDim }}>%</span>
      </div>
    </div>
  );
}

const th = () => ({ padding: "6px 8px", textAlign: "left", fontWeight: 600, borderBottom: `1px solid ${T.border}`, textTransform: "uppercase", fontSize: 10, letterSpacing: 0.5 });
const tdNome = () => ({ padding: "8px", fontWeight: 600, color: "#fff", borderBottom: `1px solid ${T.border}` });
const td = (ativo) => ({ padding: "8px", textAlign: "right", color: ativo ? T.brand : T.textDim, borderBottom: `1px solid ${T.border}`, fontWeight: ativo ? 600 : 400 });
